import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { addDays, addMonths, eachDayOfInterval, endOfMonth, isAfter, parseISO, startOfDay, startOfMonth } from "date-fns";
import { nn } from "date-fns/locale";
import { ArrowRight, CalendarDays, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Container, Eyebrow } from "@/components/shared/PageHeader";
import { fetchAvailability, fetchCalendar, imgUrl } from "@/lib/api";
import { fmtDate, fmtPrice, floorLabel, minPrice, toISODate } from "@/lib/format";
import { practicesOn } from "@/lib/recurring";
import { cn } from "@/lib/utils";

const DAYS_AHEAD = 14;
const WEEKDAYS_SHORT = ["må", "ty", "on", "to", "fr", "la", "su"];
const jsWeekdayToPy = (d) => (d.getDay() + 6) % 7;

const roomMeta = (r) => {
  if (r.is_whole_house) return "Alle etasjar";
  return [floorLabel(r.floor), r.capacity ? `${r.capacity} pers.` : r.capacity_note].filter(Boolean).join(" · ");
};
const roomLabel = (r) => r?.short_name || r?.name || "";

/**
 * Availability check on the home page – calm and editorial.
 * Left: the answer in plain words + the call to action.
 * Right: one quiet surface with two steps – a hairline grid of rooms and a real two-week calendar.
 * Everything carries over to the booking form (/leig-rom?rom=…&dato=…).
 */
export const QuickBook = ({ rooms, fallbackImage, className }) => {
  const navigate = useNavigate();
  const [roomId, setRoomId] = useState("");
  const [date, setDate] = useState(undefined);
  const [calOpen, setCalOpen] = useState(false);
  const today = useMemo(() => startOfDay(new Date()), []);
  const [month, setMonth] = useState(startOfMonth(today));

  const bookable = useMemo(() => (rooms || []).filter((r) => r.bookable), [rooms]);
  const room = bookable.find((r) => r.id === roomId);
  const iso = date ? toISODate(date) : "";
  const days = useMemo(() => Array.from({ length: DAYS_AHEAD }, (_, i) => addDays(today, i)), [today]);
  // Align the 14 days to real weekday columns (Monday first), padded to whole weeks.
  const leading = jsWeekdayToPy(today);
  const trailing = (7 - ((leading + DAYS_AHEAD) % 7)) % 7;

  const calFrom = toISODate(startOfMonth(today));
  const calTo = toISODate(endOfMonth(addMonths(isAfter(month, today) ? month : today, 1)));
  const calendar = useQuery({
    queryKey: ["calendar", calFrom, calTo, roomId],
    queryFn: () => fetchCalendar({ from: calFrom, to: calTo, room_id: roomId }),
    enabled: !!roomId,
  });
  const busyDays = useMemo(() => {
    const out = [];
    for (const e of calendar.data?.entries || []) {
      try { out.push(...eachDayOfInterval({ start: parseISO(e.start_date), end: parseISO(e.end_date || e.start_date) })); } catch { /* ignore */ }
    }
    return out;
  }, [calendar.data]);
  const busySet = useMemo(() => new Set(busyDays.map(toISODate)), [busyDays]);
  const practices = calendar.data?.recurring || [];
  const practiceHits = date ? practicesOn(practices, date, roomId) : [];

  const availability = useQuery({
    queryKey: ["availability", roomId, iso],
    queryFn: () => fetchAvailability({ room_ids: roomId, start_date: iso }),
    enabled: !!roomId && !!iso,
  });
  const av = availability.data;
  const checking = !!roomId && !!iso && (availability.isFetching || !av);

  const nextFree = useMemo(() => {
    if (!date || !roomId || !av || av.available) return [];
    const limit = parseISO(calTo);
    const out = [];
    for (let i = 1; i <= 60 && out.length < 3; i += 1) {
      const d = addDays(date, i);
      if (isAfter(d, limit)) break;
      if (!busySet.has(toISODate(d))) out.push(d);
    }
    return out;
  }, [date, roomId, av, busySet, calTo]);

  const pickDate = (d) => {
    setDate(d);
    if (d) setMonth(startOfMonth(d));
    setCalOpen(false);
  };
  const go = () => {
    const p = new URLSearchParams();
    if (roomId) p.set("rom", roomId);
    if (iso) p.set("dato", iso);
    navigate(`/leig-rom${p.toString() ? `?${p}` : ""}`);
  };

  // ---- Copy ---------------------------------------------------------------
  const price = room ? minPrice(room) : null;
  let headline; let sub; let tone = "idle"; let cta = "Gå til booking";
  if (!room) {
    headline = <>Finn eit rom som <em className="t-swash">passar</em></>;
    sub = "Vel rom og dag, så ser du med ein gong om det er ledig – utan å røpe kven som har booka.";
  } else if (!iso) {
    headline = <>Når vil du bruke <em className="t-swash">{roomLabel(room)}</em>?</>;
    sub = calendar.isFetching ? "Hentar kalenderen …" : "Ledige dagar er merkte med grøn prikk, opptekne med raud.";
    cta = "Hald fram til booking";
  } else if (checking) {
    headline = "Sjekkar kalenderen …";
    sub = `${roomLabel(room)} · ${fmtDate(iso, "EEEE d. MMMM")}`;
    cta = "Hald fram til booking";
  } else if (av.available) {
    tone = "ok";
    headline = <>{roomLabel(room)} er <em className="t-swash">ledig</em> {fmtDate(iso, "EEEE d. MMMM")}</>;
    sub = [price ? `frå ${fmtPrice(price)}` : "pris etter avtale", roomMeta(room)].join(" · ");
    cta = "Hald fram til booking";
  } else {
    tone = "busy";
    headline = <>{roomLabel(room)} er oppteke {fmtDate(iso, "d. MMMM")}</>;
    sub = av.conflicts?.[0]?.title ? `${av.conflicts[0].title}${av.conflicts.length > 1 ? ` + ${av.conflicts.length - 1} til` : ""}` : "Dagen er reservert.";
    cta = "Hald fram likevel";
  }

  const monthTag = (d, i) => (i === 0 || d.getDate() === 1 ? fmtDate(toISODate(d), "MMM").replace(".", "") : null);

  return (
    <section className={cn("scroll-mt-24 overflow-x-clip pb-20 pt-4 sm:pb-24 sm:pt-6 lg:pb-28 lg:pt-8", className)} aria-label="Sjekk om det er ledig" data-testid="quick-book" id="ledig">
      <Container>
        <div className="grid gap-10 border-t border-border pt-14 sm:pt-16 lg:grid-cols-12 lg:grid-rows-[auto_1fr] lg:gap-x-16 lg:gap-y-10 lg:pt-20">
          {/* ------------------------------------------------ The answer */}
          <div className="order-1 min-w-0 lg:order-none lg:col-span-5 lg:col-start-1 lg:row-start-1">
            <Eyebrow>Sjekk om det er ledig</Eyebrow>
            <h2 className="t-h2 mt-5 text-balance" data-testid="quick-book-status" aria-live="polite">
              {checking && <Loader2 className="mr-2 inline h-5 w-5 animate-spin text-muted-foreground align-[-3px]" aria-hidden="true" />}
              {headline}
            </h2>
            <p className="mt-5 flex max-w-md items-start gap-2.5 text-[16px] leading-relaxed text-muted-foreground text-pretty" data-testid="quick-book-subline">
              {tone !== "idle" && <span className={cn("mt-2 h-2.5 w-2.5 shrink-0 rounded-full", tone === "ok" ? "bg-ok" : "bg-destructive")} aria-hidden="true" />}
              <span>{sub}</span>
            </p>
            {tone === "busy" && nextFree.length > 0 && (
              <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[15px]">
                <span className="text-muted-foreground">Neste ledige:</span>
                {nextFree.map((d) => (
                  <button key={toISODate(d)} type="button" onClick={() => pickDate(d)} className="u-link font-medium" data-testid={`quick-book-next-${toISODate(d)}`}>{fmtDate(toISODate(d), "EEEE d. MMM")}</button>
                ))}
              </p>
            )}
            {tone === "ok" && practiceHits.length > 0 && (
              <p className="mt-3 max-w-md text-[14px] leading-relaxed text-muted-foreground" data-testid="quick-book-practice-hint">Merk: fast aktivitet same dag ({practiceHits.map((p) => `${p.group_name || p.title} kl. ${p.start_time}–${p.end_time}`).join(", ")}).</p>
            )}
          </div>

          {/* ------------------------------------------------ The action (under the answer on desktop, after the panel on mobile) */}
          <div className="order-3 min-w-0 self-start lg:order-none lg:col-span-5 lg:col-start-1 lg:row-start-2">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
              <Button onClick={go} size="lg" className="w-full justify-between sm:w-auto sm:min-w-[260px]" data-testid="quick-book-submit">{cta} <ArrowRight className="h-4 w-4" /></Button>
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <button type="button" className="group inline-flex min-h-[44px] items-center gap-2 text-[15px] font-medium" data-testid="quick-book-date-trigger">
                    <CalendarDays className="h-4 w-4" /> <span className="u-hover">Sjå heile kalenderen</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto rounded-[12px] p-2" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={pickDate}
                    month={month}
                    onMonthChange={setMonth}
                    locale={nn}
                    weekStartsOn={1}
                    disabled={{ before: today }}
                    modifiers={{ busy: busyDays }}
                    modifiersClassNames={{ busy: "day-busy" }}
                    data-testid="quick-book-calendar"
                  />
                  <p className="flex items-center gap-4 px-3 pb-2 pt-1 text-[12px] text-muted-foreground">
                    <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-foreground" aria-hidden="true" /> Oppteke{roomId ? "" : " (vel rom først)"}</span>
                    {calendar.isFetching && <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> hentar</span>}
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">Førespurnaden er ikkje bindande. Styret stadfestar bookinga på e-post.</p>
          </div>

          {/* ------------------------------------------------ The controls: one quiet surface */}
          <div className="order-2 min-w-0 overflow-hidden rounded-[16px] border border-border bg-card lg:order-none lg:col-span-7 lg:col-start-6 lg:row-span-2 lg:row-start-1" data-testid="quick-book-panel">
            {/* Step 1 – rooms as a hairline grid */}
            <div className="px-5 pt-5 xl:px-7 xl:pt-7">
              <div className="flex items-baseline justify-between gap-4">
                <p className="t-mono text-muted-foreground"><span className="tabular">01</span> &nbsp;Rom</p>
                {room && <button type="button" onClick={() => setRoomId("")} className="text-[13px] text-muted-foreground u-hover" data-testid="quick-book-room-clear">Nullstill</button>}
              </div>
            </div>
            <div className="mt-4 grid gap-px border-y border-border bg-border sm:grid-cols-2" role="radiogroup" aria-label="Rom" data-testid="quick-book-rooms">
              {bookable.length === 0 && [0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="flex h-[76px] items-center gap-4 bg-card px-5 xl:px-7"><span className="h-11 w-11 animate-pulse rounded-[8px] bg-secondary" /><span className="h-3 w-28 animate-pulse rounded bg-secondary" /></div>)}
              {bookable.map((r, idx) => {
                const sel = r.id === roomId;
                const img = r.is_whole_house ? fallbackImage || r.images?.[0] : r.images?.[0];
                const p = minPrice(r);
                const lastOdd = idx === bookable.length - 1 && bookable.length % 2 === 1;
                return (
                  <button
                    key={r.id}
                    type="button"
                    role="radio"
                    aria-checked={sel}
                    onClick={() => setRoomId(sel ? "" : r.id)}
                    className={cn(
                      "group relative flex min-h-[76px] items-center gap-3 px-5 py-3.5 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foreground xl:gap-4 xl:px-7",
                      sel ? "bg-secondary/80" : "bg-card hover:bg-secondary/50",
                      lastOdd && "sm:col-span-2"
                    )}
                    data-testid={`quick-book-room-${r.slug}`}
                  >
                    {sel && <span className="absolute bottom-3 left-0 top-3 w-[3px] rounded-full bg-sol" aria-hidden="true" />}
                    {img ? <img src={imgUrl(img, 120)} alt="" className="h-11 w-11 shrink-0 rounded-[8px] object-cover" loading="lazy" decoding="async" /> : <span className="h-11 w-11 shrink-0 rounded-[8px] bg-secondary" />}
                    <span className="min-w-0 flex-1 leading-tight">
                      <span className="block truncate text-[15px] font-medium">{roomLabel(r)}</span>
                      <span className="mt-0.5 block truncate text-[13px] text-muted-foreground">{roomMeta(r)}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      {!sel && <span className="hidden whitespace-nowrap text-[13px] text-muted-foreground tabular xl:inline">{p ? `frå ${fmtPrice(p)}` : "etter avtale"}</span>}
                      <span className={cn("flex h-6 w-6 items-center justify-center rounded-full border transition-colors", sel ? "border-foreground bg-foreground text-background" : "border-border text-transparent group-hover:border-foreground/40")} aria-hidden="true"><Check className="h-3.5 w-3.5" strokeWidth={2.5} /></span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Step 2 – a real two-week calendar */}
            <div className="px-5 pb-6 pt-6 xl:px-7 xl:pb-7 xl:pt-7">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
                <p className="t-mono text-muted-foreground"><span className="tabular">02</span> &nbsp;Dag</p>
                <p className="text-[13px] text-muted-foreground" aria-hidden="true">
                  {roomId ? (
                    <span className="inline-flex items-center gap-4"><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-ok" /> ledig</span><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-destructive" /> oppteke</span></span>
                  ) : "Dei neste to vekene"}
                </p>
              </div>
              <div className="mt-4 grid grid-cols-7 text-center" aria-hidden="true">
                {WEEKDAYS_SHORT.map((w) => <span key={w} className="t-mono-sm pb-2 text-muted-foreground">{w}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-1" role="radiogroup" aria-label="Dag" data-testid="quick-book-days">
                {Array.from({ length: leading }).map((_, i) => <span key={`lead-${i}`} aria-hidden="true" />)}
                {days.map((d, i) => {
                  const dIso = toISODate(d);
                  const busy = busySet.has(dIso);
                  const sel = dIso === iso;
                  const tag = monthTag(d, i);
                  return (
                    <button
                      key={dIso}
                      type="button"
                      role="radio"
                      aria-checked={sel}
                      aria-label={`${fmtDate(dIso, "EEEE d. MMMM")}${roomId ? (busy ? " – oppteke" : " – ledig") : ""}`}
                      onClick={() => pickDate(sel ? undefined : d)}
                      className={cn(
                        "relative flex aspect-square min-h-[44px] flex-col items-center justify-center rounded-[10px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 sm:aspect-[5/4]",
                        sel ? "bg-foreground text-background" : busy && roomId ? "text-muted-foreground hover:bg-secondary/60" : "hover:bg-secondary"
                      )}
                      data-testid={`quick-book-day-${dIso}`}
                    >
                      <span className={cn("font-display text-[20px] font-semibold leading-none tabular sm:text-[22px]", busy && roomId && !sel && "line-through decoration-1 decoration-muted-foreground/60")}>{d.getDate()}</span>
                      <span className={cn("mt-1 text-[10.5px] leading-none", sel ? "text-background/70" : "text-muted-foreground", !tag && i !== 0 && "invisible")}>{i === 0 ? "i dag" : tag}</span>
                      {roomId && <span className={cn("absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full", sel ? "bg-background/80" : busy ? "bg-destructive" : "bg-ok")} aria-hidden="true" />}
                    </button>
                  );
                })}
                {Array.from({ length: trailing }).map((_, i) => (
                  i === trailing - 1 ? (
                    <button key="more" type="button" onClick={() => setCalOpen(true)} className="flex aspect-square min-h-[44px] flex-col items-center justify-center rounded-[10px] border border-dashed border-border text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground sm:aspect-[5/4]" aria-label="Vel ein annan dato" data-testid="quick-book-more-dates">
                      <CalendarDays className="h-4 w-4" />
                      <span className="mt-1 text-[10.5px] leading-none">fleire</span>
                    </button>
                  ) : <span key={`trail-${i}`} aria-hidden="true" />
                ))}
                {trailing === 0 && (
                  <button type="button" onClick={() => setCalOpen(true)} className="col-span-7 mt-1 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] border border-dashed border-border text-[13px] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground" aria-label="Vel ein annan dato" data-testid="quick-book-more-dates">
                    <CalendarDays className="h-4 w-4" /> Fleire datoar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
